/**
 * Hook providing timezone-aware date formatters using auth context.
 * 
 * Returns null if user is not authenticated (prevents logout crash).
 * THROWS if user IS authenticated but timezone is not set.
 */
import { useMemo } from 'react';
import useAuth from 'hooks/useAuth';
import { formatInTimezone, formatRelativeTime } from 'utils/dateUtils';

export default function useUserTimezone() {
    const { user, isInitialized, isLoggedIn } = useAuth();

    // Return null during logout/unauthenticated states to prevent crash
    // Components using this hook should handle null gracefully
    if (!isInitialized || !isLoggedIn) {
        return null;
    }

    const timezone = user?.timezone;

    // No fallback - throw if timezone not set (but user IS authenticated)
    if (!timezone) {
        throw new Error(
            'useUserTimezone: user.timezone is not set. ' +
            'Ensure timezone is configured in user profile settings.'
        );
    }

    return useMemo(() => ({
        // Format: "Jan 13, 2:30 PM"
        formatDateTime: (utcDate) => formatInTimezone(utcDate, 'MMM d, h:mm a', timezone),
        // Format: "Jan 13, 2026"
        formatDate: (utcDate) => formatInTimezone(utcDate, 'MMM d, yyyy', timezone),

        // Format: "2:30 PM"
        formatTime: (utcDate) => formatInTimezone(utcDate, 'h:mm a', timezone),

        // Format: "5 minutes ago"
        formatRelative: (utcDate) => formatRelativeTime(utcDate, timezone),

        // Format: "Jan 13, 2026, 2:30 PM"
        formatFull: (utcDate) => formatInTimezone(utcDate, 'MMM d, yyyy, h:mm a', timezone),

        // Raw timezone string for display
        timezone,
    }), [timezone]);
}
